export const ProductCategory=Object.freeze({
  PORK:'pork',
  BEEF:'beef',
  POULTRY:'poultry',
  FISH:'fish',
  SAUSAGE:'sausage',
  CHEESE:'cheese',
  VEGETABLES:'vegetables',
  OTHER:'other'
});

export const PRODUCT_CATEGORIES=Object.freeze(Object.values(ProductCategory));
const CATEGORY_SET=new Set(PRODUCT_CATEGORIES);
const CODE=/^[a-z][a-z0-9_]{1,63}$/;

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function category(value){
  const key=String(value||'').trim().toLowerCase();
  if(!CATEGORY_SET.has(key))throw new TypeError('Invalid product category');
  return key;
}

function stableCode(value){
  const code=String(value||'').trim().toLowerCase();
  if(!CODE.test(code))throw new TypeError('Invalid product code');
  return code;
}

function localeKey(locale){
  return String(locale||'pl').toLowerCase().startsWith('en')?'en':'pl';
}

function fold(value){
  return String(value||'')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g,'')
    .toLocaleLowerCase('pl')
    .trim();
}

export function createCatalogueProduct({
  code,
  categoryKey,
  displayNamePl,
  displayNameEn,
  sortOrder=100,
  catalogueVersion=1
}={}){
  const order=Number(sortOrder);
  const version=Number(catalogueVersion);
  if(!Number.isInteger(order)||order<0||order>100000)throw new TypeError('Invalid sortOrder');
  if(!Number.isInteger(version)||version<1)throw new TypeError('Invalid catalogueVersion');
  return Object.freeze({
    code:stableCode(code),
    categoryKey:category(categoryKey),
    labels:Object.freeze({
      pl:required(displayNamePl,'displayNamePl',120),
      en:required(displayNameEn,'displayNameEn',120)
    }),
    sortOrder:order,
    catalogueVersion:version,
    source:'catalogue'
  });
}

export function catalogueProductFromRow(row={}, {locale='pl'}={}){
  const item=createCatalogueProduct({
    code:row.code,
    categoryKey:row.category_key,
    displayNamePl:row.display_name_pl,
    displayNameEn:row.display_name_en,
    sortOrder:row.sort_order,
    catalogueVersion:row.catalogue_version
  });
  const lang=localeKey(locale);
  return Object.freeze({...item,displayName:item.labels[lang]});
}

export function createCustomProduct({
  id,
  userId,
  name,
  categoryKey='other',
  archivedAt=null
}={}){
  const productId=required(id,'id',128);
  return Object.freeze({
    id:productId,
    userId:required(userId,'userId',128),
    name:required(name,'name',120),
    categoryKey:category(categoryKey),
    archivedAt:archivedAt?String(archivedAt):null,
    source:'custom'
  });
}

export function customProductToRow(product){
  const value=createCustomProduct(product);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    name:value.name,
    category_key:value.categoryKey,
    archived_at:value.archivedAt
  });
}

export function customProductFromRow(row={}){
  return createCustomProduct({
    id:row.id,
    userId:row.user_id,
    name:row.name,
    categoryKey:row.category_key,
    archivedAt:row.archived_at
  });
}

export function searchCatalogueProducts(products,{query='',categoryKey=null,locale='pl'}={}){
  if(!Array.isArray(products))throw new TypeError('products must be an array');
  const lang=localeKey(locale);
  const wantedCategory=categoryKey?category(categoryKey):null;
  const term=fold(query);

  return Object.freeze(
    products
      .map(item=>item?.labels?item:createCatalogueProduct(item))
      .filter(item=>!wantedCategory||item.categoryKey===wantedCategory)
      .filter(item=>!term||fold(item.labels[lang]).includes(term)||fold(item.code).includes(term))
      .sort((a,b)=>a.sortOrder-b.sortOrder||a.labels[lang].localeCompare(b.labels[lang],lang))
      .map(item=>Object.freeze({...item,displayName:item.labels[lang]}))
  );
}
